import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { getUrlParamsFetch, notFoundIdHandler } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { TipoFacturacion, TipoFacturacionPagingRes } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchTipoFacturacions = (
  params?: GetTipoFacturacionsParams
) => {
  return useQuery({
    queryKey: ['tipoFacturacions', ...Object.values(params || {})],
    queryFn: async () => {
      return getTipoFacturacions(params);
    },
  });
};

export const useGetTipoFacturacion = (id: string) => {
  return useQuery({
    queryKey: ['tipoFacturacion', id],
    queryFn: async () => {
      return getTipoFacturacion(id);
    },
  });
};

export const useCreateTipoFacturacion = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTipoFacturacion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipoFacturacions'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdateTipoFacturacion = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTipoFacturacion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipoFacturacions'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

///* axios
export type GetTipoFacturacionsParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
  codigo?: string;
};

export type CreateTipoFacturacionParams = Omit<
  TipoFacturacion,
  'id_tipo_facturacion'
>;
export type UpdateTipoFacturacionParams = {
  id: string;
  data: CreateTipoFacturacionParams;
};

export const getTipoFacturacions = async (
  params?: GetTipoFacturacionsParams
) => {
  const queryParams = getUrlParamsFetch(params || {});

  return get<TipoFacturacionPagingRes>(
    `${urlBackend}api/v1/tipos-facturacion/?${queryParams}`,
    true
  );
};

export const getTipoFacturacion = async (id: string) => {
  const res = await get<TipoFacturacion>(
    `${urlBackend}api/v1/tipos-facturacion/${id}/`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createTipoFacturacion = async (
  data: CreateTipoFacturacionParams
) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<TipoFacturacion>(
    `${urlBackend}api/v1/tipos-facturacion/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateTipoFacturacion = async ({
  id,
  data,
}: UpdateTipoFacturacionParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<TipoFacturacion>(
    `${urlBackend}api/v1/tipos-facturacion/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
